import { Modal } from "@/components/Modal";
import type { Client, ClientInput } from "@/hooks/useClientsData";
import { useState } from "react";

interface ClientModalProps {
  /** When provided, the modal edits this client; otherwise it creates a new one. */
  client?: Client;
  /** Number of projects linked to this client; blocks deletion when > 0. */
  projectCount?: number;
  onClose: () => void;
  onSave: (input: ClientInput) => Promise<void>;
  /** Delete the client. Provided only in edit mode. Rejects on failure. */
  onDelete?: () => Promise<void>;
}

export function ClientModal({
  client,
  projectCount = 0,
  onClose,
  onSave,
  onDelete,
}: ClientModalProps) {
  const [name, setName] = useState(client?.name ?? "");
  const [location, setLocation] = useState(client?.location ?? "");
  const [contactName, setContactName] = useState(client?.contact_name ?? "");
  const [contactEmail, setContactEmail] = useState(client?.contact_email ?? "");
  const [contactPhone, setContactPhone] = useState(client?.contact_phone ?? "");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isValid = name.trim().length > 0;
  const hasProjects = projectCount > 0;

  const handleDelete = async () => {
    if (!onDelete || hasProjects || deleting || saving) return;
    if (!window.confirm(`Delete client "${client?.name}"?`)) return;
    setDeleting(true);
    try {
      await onDelete();
      // On success the parent closes this modal.
    } catch {
      setDeleting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || saving) return;

    setSaving(true);
    setError(null);
    try {
      await onSave({
        name: name.trim(),
        location: location.trim() || null,
        contact_name: contactName.trim() || null,
        contact_email: contactEmail.trim() || null,
        contact_phone: contactPhone.trim() || null,
      });
      onClose();
    } catch (err) {
      // Unique-name violation surfaces as Postgres error 23505.
      const code = (err as { code?: string })?.code;
      setError(
        code === "23505"
          ? `A client named "${name.trim()}" already exists.`
          : err instanceof Error
            ? err.message
            : "Failed to save client",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={client ? "Edit Client" : "Add Client"} onClose={onClose} size="small">
      <form className="add-employee-form" onSubmit={handleSubmit}>
        {error && <div className="add-employee-form__error">{error}</div>}

        <label className="add-employee-form__field">
          <span className="add-employee-form__label">Name</span>
          <input
            type="text"
            className="add-employee-form__input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Client name"
            autoFocus
          />
        </label>

        <label className="add-employee-form__field">
          <span className="add-employee-form__label">Location</span>
          <input
            type="text"
            className="add-employee-form__input"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Location name"
          />
        </label>

        <fieldset className="add-employee-form__field">
          <legend className="add-employee-form__label">Primary contact</legend>
          <input
            type="text"
            className="add-employee-form__input"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            placeholder="Contact name"
          />
          <input
            type="email"
            className="add-employee-form__input"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="Contact email"
          />
          <input
            type="tel"
            className="add-employee-form__input"
            value={contactPhone}
            onChange={(e) => setContactPhone(e.target.value)}
            placeholder="Contact phone"
          />
        </fieldset>

        {client && onDelete && hasProjects && (
          <p className="add-employee-form__note">
            Clients with projects still attached to them cannot be deleted
          </p>
        )}

        <div className="modal-actions modal-actions--between">
          {client && onDelete ? (
            <button
              type="button"
              className="danger-button"
              onClick={handleDelete}
              disabled={hasProjects || saving || deleting}
            >
              {deleting ? "Deleting..." : "Delete"}
            </button>
          ) : (
            <span />
          )}
          <div className="modal-actions__group">
            <button
              type="button"
              className="secondary-button"
              onClick={onClose}
              disabled={saving || deleting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="primary-button"
              disabled={!isValid || saving || deleting}
            >
              {saving ? "Saving..." : client ? "Save Changes" : "Add Client"}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
